import React, { lazy, type ComponentType } from "react";
import AppScaffold from "../components/AppScaffold";
import type { Config, EntryMapping } from "../config/client";
import { traceManager } from "../sentry";
import { accountLayoutConfig } from "./account/layoutConfig";
import { accountAdminLayoutConfig } from "./accountAdmin/layoutConfig";
import { dashboardLayoutConfig } from "./dashboard/layoutConfig";
import { InitialLoader } from "./legacyEngage/engageComponents/InitialLoader";
import LoadingScreen from "./messagemedia/components/LoadingScreen";

const getLazyComponent = (p: () => Promise<ComponentType>) =>
  lazy<ComponentType>(async () => {
    const transaction = traceManager.getTransaction("TimeToFirstMFELoad");
    if (transaction) {
      transaction.start("fetch-portal-entry");
    }
    const result = await p();
    traceManager.endTrace("fetch-portal-entry");
    return { default: result };
  });
interface RenderShellOpts {
  App: ReturnType<typeof getLazyComponent>;
  Loader: React.ComponentType;
}

type OptionsFn = () => RenderShellOpts;

const optionsFns = {
  engage(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./legacyEngage/App").then(({ App }) => App)
      ),
      Loader: InitialLoader,
    };
  },
  mm(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./messagemedia/App").then(({ App }) => App)
      ),
      Loader: LoadingScreen,
    };
  },
  cop(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./cop/App").then((x) => x.WrappedApp)
      ),
      Loader: () => <AppScaffold withSidebar />,
    };
  },
  my(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./my/App").then(({ WrappedApp }) => WrappedApp)
      ),
      Loader: function Loader() {
        return <AppScaffold withSidebar />;
      },
    };
  },
  dashboard(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./dashboard/App").then(({ WrappedApp }) => WrappedApp)
      ),
      Loader: function Loader() {
        return (
          <AppScaffold
            withSidebar
            withTopBar
            layoutConfig={dashboardLayoutConfig}
          />
        );
      },
    };
  },
  connect(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./connect/App").then(({ WrappedApp }) => WrappedApp)
      ),
      Loader: function Loader() {
        return <AppScaffold withSidebar />;
      },
    };
  },
  account(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./account/App").then(({ WrappedApp }) => WrappedApp)
      ),
      Loader: function Loader() {
        return (
          <AppScaffold
            withTopBar
            withSidebar
            layoutConfig={accountLayoutConfig}
          />
        );
      },
    };
  },
  accountAdmin(): RenderShellOpts {
    return {
      App: getLazyComponent(() =>
        import("./accountAdmin/App").then(({ WrappedApp }) => WrappedApp)
      ),
      Loader: function Loader() {
        return (
          <AppScaffold
            withTopBar
            withSidebar
            layoutConfig={accountAdminLayoutConfig}
          />
        );
      },
    };
  },
} as const satisfies EntryMapping<OptionsFn>;

export const getRenderShellOptions: (config: Config) => RenderShellOpts = (
  config: Config
) => config.selectByEntry(optionsFns)();
